/**
 * Session form (participants).
 */
import * as React from 'react';
import { Users } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { UsersListColumn } from '@/components/common/UsersListColumn';
import { UnifiedDropZone } from '@/components/common/UnifiedDropZone';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateStudentView } from '@/components/students/inperson/CreateStudentView';
import { TutoringSessionOrganizerPickerModal } from './TutoringSessionOrganizerPickerModal';
import { prependUserToUsersSearchCaches } from '@/utils/created-user-profile-utils';
import type { UserWithProfile } from '@/types/user.types';
import type { SlotAvailabilityById } from '@/types/slot-availability.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { ParticipantAttendanceById } from '@/types/attendance.types';

/** Props for {@link TutoringSessionParticipantsSection}. */
interface TutoringSessionParticipantsSectionProps {
  selectedTutors: UserWithProfile[];
  selectedStudents: UserWithProfile[];
  selectedTutorIds: string[];
  selectedStudentIds: string[];
  maxTutors: number | null;
  maxStudents: number | null;
  onRemoveTutor: (userId: string) => void;
  onRemoveStudent: (userId: string) => void;
  onTutorDoubleClick: (user: UserWithProfile) => void | Promise<void>;
  onStudentDoubleClick: (user: UserWithProfile) => void;
  onRequestCreateTutor?: () => void;
  establishmentId?: string;
  establishmentName?: string;
  /** Matching mode (edit session with pairs). */
  sessionId?: string;
  specificMatches?: Array<{ studentId: string; tutorId: string }>;
  getTutorMatchedStudents?: (tutorId: string) => Array<
    UserWithProfile & {
      assignmentSource?: 'manual' | 'auto_binome_coeur' | 'auto_history' | 'auto_balanced';
    }
  >;
  dropZoneMode?: 'unified' | 'matching';
  studentsOverCapacity?: number;
  tutorAvailabilityById?: SlotAvailabilityById;
  tutorAvailabilityHint?: string | null;
  tutorAvailabilityChecking?: boolean;
  studentAvailabilityById?: SlotAvailabilityById;
  studentAvailabilityHint?: string | null;
  studentAvailabilityChecking?: boolean;
  /** When true, tutors column shows not-allowed cursor + tooltip */
  tutorDragDisabled?: boolean;
  /** Tooltip message shown on tutor cards when tutorDragDisabled is true */
  tutorDragDisabledTooltip?: string;
  /** When true, students column shows not-allowed cursor + tooltip */
  studentDragDisabled?: boolean;
  /** Tooltip message shown on student cards when studentDragDisabled is true */
  studentDragDisabledTooltip?: string;
  /** Day of week of the session (0=Sunday..6=Saturday) for presence-day filtering */
  sessionAvailabilityDay?: number;
  /** Attendance by participant (same tag as session detail). */
  attendanceByUserId?: ParticipantAttendanceById;
}

/** Session form (participants). */
export const TutoringSessionParticipantsSection: React.FC<
  TutoringSessionParticipantsSectionProps
> = ({
  selectedTutors,
  selectedStudents,
  selectedTutorIds,
  selectedStudentIds,
  maxTutors,
  maxStudents,
  onRemoveTutor,
  onRemoveStudent,
  onTutorDoubleClick,
  onStudentDoubleClick,
  onRequestCreateTutor,
  establishmentId,
  establishmentName,
  sessionId,
  specificMatches,
  getTutorMatchedStudents,
  dropZoneMode = 'unified',
  studentsOverCapacity = 0,
  tutorAvailabilityById,
  tutorAvailabilityHint,
  tutorAvailabilityChecking = false,
  studentAvailabilityById,
  studentAvailabilityHint,
  studentAvailabilityChecking = false,
  tutorDragDisabled = false,
  tutorDragDisabledTooltip,
  studentDragDisabled = false,
  studentDragDisabledTooltip,
  sessionAvailabilityDay,
  attendanceByUserId,
}) => {
  const queryClient = useQueryClient();
  const [isFacilitatorCreationOpen, setIsFacilitatorCreationOpen] = React.useState(false);
  const [isStudentPanelOpen, setIsStudentPanelOpen] = React.useState(false);

  const tutorBulkToastRef = React.useRef<{ dismiss: () => void } | null>(null);
  const studentBulkToastRef = React.useRef<{ dismiss: () => void } | null>(null);

  React.useEffect(() => {
    if (tutorAvailabilityChecking) {
      tutorBulkToastRef.current?.dismiss();
      tutorBulkToastRef.current = toast.loading(getErrorMessage('tutorAvailabilityChecking'));
    } else {
      tutorBulkToastRef.current?.dismiss();
      tutorBulkToastRef.current = null;
      if (tutorAvailabilityHint) {
        toast.warning(getErrorMessage(tutorAvailabilityHint), { duration: 6000 });
      }
    }
    return () => {
      tutorBulkToastRef.current?.dismiss();
    };
  }, [tutorAvailabilityChecking, tutorAvailabilityHint]);

  React.useEffect(() => {
    if (studentAvailabilityChecking) {
      studentBulkToastRef.current?.dismiss();
      studentBulkToastRef.current = toast.loading(getErrorMessage('studentAvailabilityChecking'));
    } else {
      studentBulkToastRef.current?.dismiss();
      studentBulkToastRef.current = null;
      if (studentAvailabilityHint) {
        toast.warning(getErrorMessage(studentAvailabilityHint), { duration: 6000 });
      }
    }
    return () => {
      studentBulkToastRef.current?.dismiss();
    };
  }, [studentAvailabilityChecking, studentAvailabilityHint]);

  /** Seed the users-search cache so the new organizer appears immediately. */
  const handleOrganizerCreatedSuccess = React.useCallback(
    (created: UserWithProfile) => {
      prependUserToUsersSearchCaches(queryClient, 'tutor', created);
    },
    [queryClient],
  );

  const handleStudentCreatedSuccess = React.useCallback(
    (created: UserWithProfile) => {
      setIsStudentPanelOpen(false);
      prependUserToUsersSearchCaches(queryClient, 'student', created);
    },
    [queryClient],
  );

  if (!establishmentId) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-[var(--color-1-nuance-3)] p-4 text-center text-sm text-muted-foreground">
        Sélectionnez un établissement pour afficher les tuteurs et élèves disponibles.
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 min-h-0 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-3 min-h-0">
          <UsersListColumn
            role="tutor"
            emptyMessage="Aucun tuteur disponible"
            excludedIds={selectedTutorIds}
            pageSize={5}
            onDoubleClick={onTutorDoubleClick}
            onCreateClick={() => setIsFacilitatorCreationOpen(true)}
            initialEstablishmentId={establishmentId}
            initialEstablishmentName={establishmentName}
            dragDisabled={tutorDragDisabled}
            dragDisabledTooltip={tutorDragDisabledTooltip}
            sessionAvailabilityDay={sessionAvailabilityDay}
          />
        </div>

        <div className="col-span-12 lg:col-span-6 min-h-0">
          <div className="section-card h-full flex flex-col">
            <div className="section-header flex-shrink-0">
              <h3 className="section-title text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Participants
              </h3>
              {(selectedTutors.length > 0 || selectedStudents.length > 0) && (
                <span className="text-sm font-medium text-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] px-2 py-1 rounded-full">
                  {selectedTutors.length + selectedStudents.length}
                </span>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-2">
              <UnifiedDropZone
                tutors={selectedTutors}
                students={selectedStudents}
                maxTutors={maxTutors}
                maxStudents={maxStudents}
                onRemoveTutor={onRemoveTutor}
                onRemoveStudent={onRemoveStudent}
                sessionId={sessionId}
                specificMatches={specificMatches}
                getTutorMatchedStudents={getTutorMatchedStudents}
                mode={dropZoneMode}
                studentsOverCapacity={studentsOverCapacity}
                tutorAvailabilityById={tutorAvailabilityById}
                studentAvailabilityById={studentAvailabilityById}
                attendanceByUserId={attendanceByUserId}
                tutorSectionLabel="Animateurs"
                tutorGroupByRole
              />
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-3 min-h-0">
          <UsersListColumn
            role="student"
            emptyMessage="Aucun élève disponible"
            excludedIds={selectedStudentIds}
            pageSize={5}
            onDoubleClick={onStudentDoubleClick}
            onCreateClick={() => setIsStudentPanelOpen(true)}
            initialEstablishmentId={establishmentId}
            initialEstablishmentName={establishmentName}
            dragDisabled={studentDragDisabled}
            dragDisabledTooltip={studentDragDisabledTooltip}
            sessionAvailabilityDay={sessionAvailabilityDay}
          />
        </div>
      </div>

      <TutoringSessionOrganizerPickerModal
        open={isFacilitatorCreationOpen}
        onOpenChange={setIsFacilitatorCreationOpen}
        onOrganizerCreated={handleOrganizerCreatedSuccess}
        onRequestCreateTutor={onRequestCreateTutor}
      />

      <SlidePanel isOpen={isStudentPanelOpen} onClose={() => setIsStudentPanelOpen(false)}>
        <CreateStudentView
          onClose={() => setIsStudentPanelOpen(false)}
          onSuccess={handleStudentCreatedSuccess}
        />
      </SlidePanel>
    </>
  );
};
